import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { lp } from "../lib/urls";

const KEY = "vr_cookie_consent";
const TTL = 365 * 24 * 60 * 60 * 1000;

const readConsent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v.ts || Date.now() - v.ts > TTL) return null;
    return v;
  } catch {
    return null;
  }
};

const CATS = ["functional", "performance", "targeting", "social"];

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => !readConsent());
  const [prefs, setPrefs] = useState({ functional: false, performance: false, targeting: false, social: false });
  if (!visible) return null;

  const persist = (p) => {
    localStorage.setItem(KEY, JSON.stringify({
      necessary: true, ...p,
      analytics: p.performance, marketing: p.targeting || p.social,
      ts: Date.now(),
    }));
    setVisible(false);
  };
  const all = () => persist({ functional: true, performance: true, targeting: true, social: true });
  const none = () => persist({ functional: false, performance: false, targeting: false, social: false });

  return (
    <div className="fixed inset-x-0 top-0 z-[70] flex justify-center px-3 pt-3 sm:pt-5">
      <div data-testid="cookie-consent-banner" className="w-full max-w-3xl bg-white text-gray-900 shadow-2xl border border-border">
        <div className="p-5 sm:p-6">
          <p className="text-sm leading-relaxed text-gray-700">
            {t("legal.banner2.text")}{" "}
            <Link to={lp("/cookie-policy")} data-testid="cookie-banner-policy-link" className="underline text-gray-900 hover:text-yellow-600 transition-colors">
              {t("legal.banner2.notice")}
            </Link>
          </p>
          <div className="mt-4 border border-border p-4">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                <input type="checkbox" checked disabled className="accent-gray-400" data-testid="cookie-cat-necessary" />
                {t("legal.banner2.necessary")}
              </label>
              {CATS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-xs sm:text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs[c]}
                    onChange={(e) => setPrefs((p) => ({ ...p, [c]: e.target.checked }))}
                    className="accent-yellow-500"
                    data-testid={`cookie-cat-${c}`}
                  />
                  {t(`legal.banner2.${c}`)}
                </label>
              ))}
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              data-testid="cookie-banner-save-btn"
              onClick={() => persist(prefs)}
              className="px-5 py-2.5 border border-gray-900 text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              {t("legal.banner2.save")}
            </button>
            <div className="sm:ml-auto flex flex-col sm:flex-row gap-3">
              <button
                data-testid="cookie-banner-necessary-btn"
                onClick={none}
                className="px-5 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
              >
                {t("legal.banner2.onlyNecessary")}
              </button>
              <button
                data-testid="cookie-banner-accept-btn"
                onClick={all}
                className="px-5 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
              >
                {t("legal.banner2.acceptAll")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
